import type { FarmEvent, FarmState, ProtocolSection, TareaRealizada } from './model';
import { addDays, todayISO, validDate, fmtDate, uid } from './dates';
import { createDomain } from './farm';
import {FEED_CHANGE_CATEGORY,isFeedChangeEvent} from './protocolCategories';

export interface HistoryEntry {
  id: string; date: string; type: string; title: string; detail: string; subject: string;
  ref?: { type: string; id: string }; area: string; scheduledDate?: string;
  completedAt?: string; legacy?: boolean; source?: string; category?: string;
}
export interface HistoryFilter { area: string; type: string; from?: string; to?: string; search?: string }
export const HISTORY_TYPES = [
  {id:'todos',label:'Todos'}, {id:'parto',label:'Partos'}, {id:'monta',label:'Montas'},
  {id:'repeticion',label:'Repeticiones'}, {id:'celo',label:'Celos'},
  {id:'alimentacion',label:'Alimentación / cambios de alimento'},
  {id:FEED_CHANGE_CATEGORY,label:'Cambios de alimento'},
  {id:'protocolo',label:'Protocolos'}, {id:'procedimiento',label:'Procedimientos aplicados'},
  {id:'manual',label:'Tareas manuales'}, {id:'destete',label:'Destetes'},
] as const;
export function historyTypeLabel(type: string): string { return HISTORY_TYPES.find(item=>item.id===type)?.label || type; }
const fold = (value: string) => value.normalize('NFD').replace(/\p{Diacritic}/gu,'').toLocaleLowerCase('es');
const taskTitle = (event: FarmEvent) => event.action || event.label.split(' — ')[1] || event.label;
function taskType(event: FarmEvent): string {
  if(isFeedChangeEvent(event)||(event.tipo==='protocolo' && /alimento|alimentacion|comida|lactancia|papilla|^fase\b/.test(fold(taskTitle(event))))) return 'alimentacion';
  return event.tipo;
}
function taskRef(state: FarmState, event: FarmEvent): HistoryEntry['ref'] {
  if(event.ref?.type!=='tarea') return event.ref ? {...event.ref} : undefined;
  const manual=state.tareasManual.find(item=>item.id===(event.manualId || event.ref?.id));
  return manual?.destinatarioTipo && manual.destinatarioId && manual.destinatarioTipo!=='general'
    ? {type:manual.destinatarioTipo,id:manual.destinatarioId} : undefined;
}

/** Reconstruct only the events explicitly present in tareasHechas, including closed cycles.
 * Dates reconstructed here are scheduled dates, never invented execution dates.
 */
export function recoverCompletedEvent(state: FarmState, key: string): FarmEvent {
  const domain=createDomain(state), parts=key.split('|'), family=parts[0];
  let ref: FarmEvent['ref'], subject='', date='', title='', detail='', section: ProtocolSection | undefined;
  let protocolId='', protocolGroup='', protocolDay: number | undefined, tipo='protocolo';
  if(family==='repro' || family==='repwin') {
    const montage=state.montas.find(item=>item.id===parts[2] && item.arete===parts[1]);
    ref={type:'cerda',id:parts[1] || ''}; subject=`Cerda ${ref.id}`;
    if(family==='repro') { section='reproduccion';protocolId=parts[3];protocolGroup='Reproducción'; }
    else { tipo='repeticion';title='Posible repetición (ventana)';detail='Revisar signos de repetición';date=validDate(parts[3] || '')?parts[3]:'';protocolGroup='Reproducción'; }
    if(section && montage) {
      const protocol=state.protocolos[section].find(item=>item.id===protocolId);
      if(protocol) date=addDays(montage.fecha,protocol.dia-1);
    }
  } else if(family==='posparto') {
    const birth=state.partos.find(item=>item.id===parts[1]);
    if(birth) { ref={type:'cerda',id:birth.arete};subject=`Cerda ${birth.arete}`; }
    section='posparto';protocolId=parts[2];protocolGroup='Maternidad';
    const protocol=state.protocolos.posparto.find(item=>item.id===protocolId);
    if(birth && protocol) date=addDays(birth.fecha,protocol.dia);
  } else if(family==='lechon' || family==='destete') {
    const litter=state.camadas.find(item=>item.id===parts[1]);
    ref={type:'camada',id:parts[1] || ''};subject=`Camada ${ref.id}`;protocolGroup='Lechones';
    if(family==='destete') { tipo='destete';title='Destete (día 21)';protocolDay=21;if(litter)date=addDays(litter.fechaNacimiento,21); }
    else {
      section='lechones';protocolId=parts[2];
      const protocol=state.protocolos.lechones.find(item=>item.id===protocolId);
      if(litter && protocol) date=addDays(litter.fechaNacimiento,protocol.dia);
    }
  } else if(family==='lote') {
    const lot=state.lotes.find(item=>item.id===parts[1]);
    ref={type:'lote',id:parts[1] || ''};subject=`Lote ${ref.id}`;section='lote';protocolId=parts[2];protocolGroup='Lote';
    const protocol=state.protocolos.lote.find(item=>item.id===protocolId);
    if(lot && protocol)date=addDays(domain.fechaNacimientoReferenciaLote(lot),protocol.dia);
  } else if(family==='celo') {
    const heat=state.celos.find(item=>item.id===parts[1]);
    tipo='celo';title='Posible presencia de celo';protocolGroup='Celo';protocolDay=Number(parts[2]);
    if(heat) { ref={type:'cerda',id:heat.arete};subject=`Cerda ${heat.arete}`;detail=heat.observaciones || 'Revisar signos de celo';date=addDays(heat.fecha,protocolDay-1); }
  } else if(family==='reemplazoCerda' || family==='reemplazoVerraco') {
    const isSow=family==='reemplazoCerda';
    const animal=isSow?domain.getCerda(parts[1]):domain.getVerraco(parts[1]);
    ref={type:isSow?'cerda':'verraco',id:parts[1] || ''};
    subject=isSow?`Cerda ${ref.id}`:`Verraco ${animal && 'nombre' in animal?animal.nombre:ref.id}`;
    section=family;protocolId=parts[2];protocolGroup=isSow?'Reemplazo de cerda':'Reemplazo de verraco';
    const protocol=state.protocolos[section].find(item=>item.id===protocolId);
    if(animal && protocol)date=addDays(animal.fechaInicioReemplazo || animal.fechaEntrada,protocol.dia-1);
  } else if(family==='manual') {
    const manual=state.tareasManual.find(item=>item.id===parts[1]);
    tipo='manual';title=manual?.titulo || 'Tarea manual eliminada';date=manual?.fecha || '';detail=manual?.detalle || '';
    subject=manual?domain.nombreDestinatarioTarea(manual):'Destinatario no conservado';
    ref={type:'tarea',id:parts[1] || ''};protocolGroup=manual?.categoria || 'Tarea general';
  }
  if(section) {
    const protocol=state.protocolos[section].find(item=>item.id===protocolId);
    title=protocol?.nombre || `Protocolo no conservado (${protocolId || family})`;
    detail=protocol?.detalle || '';protocolDay=protocol?.dia;
    if(section==='reproduccion' && protocol?.dia===114)tipo='parto';
  }
  if(!title) { title='Tarea terminada sin metadatos conservados';detail=`Clave original: ${key}`; }
  const event: FarmEvent={date,tipo,prioridad:2,label:title,action:title,detail,subject:subject || 'Sin destinatario conservado',doneKey:key,ref,protocolGroup};
  if(protocolDay!==undefined && Number.isFinite(protocolDay))event.protocolDay=protocolDay;
  if(family==='manual')event.manualId=parts[1];
  return domain.aplicarFechaPersonalizada(event);
}

export function captureCompletedTask(state: FarmState, key: string): TareaRealizada {
  const domain=createDomain(state), event=domain.generateAllEvents().find(item=>item.doneKey===key) || recoverCompletedEvent(state,key);
  const fechaRealizacion=todayISO();
  const snapshot=structuredClone(event), ref=taskRef(state,event);
  if(ref)snapshot.ref=ref;else delete snapshot.ref;
  return {id:uid('tarea'),doneKey:key,event:snapshot,area:domain.eventArea({...event,date:fechaRealizacion}),completedAt:new Date().toISOString(),fechaRealizacion};
}

/** Run on an editable draft before commands can rename/delete templates or close cycles.
 * Legacy completions have no execution timestamp: preserve what is known without fabricating one.
 */
export function preserveCompletedTasks(state: FarmState): void {
  const keys=new Set(state.tareasHechas), captured=new Set((state.tareasRealizadas || []).map(item=>item.doneKey));
  const missing=[...keys].filter(key=>!captured.has(key));
  if(!missing.length)return;
  const domain=createDomain(state), activeEvents=new Map(domain.generateAllEvents().map(event=>[event.doneKey,event]));
  const snapshots=missing.map(key=>{
    const event=activeEvents.get(key) || recoverCompletedEvent(state,key), snapshot=structuredClone(event), ref=taskRef(state,event);
    if(ref)snapshot.ref=ref;else delete snapshot.ref;
    return {doneKey:key,event:snapshot,area:domain.eventArea(event),legacy:true};
  });
  state.tareasRealizadas=[...(state.tareasRealizadas || []),...snapshots];
}

export function getHistory(state: FarmState): HistoryEntry[] {
  const domain=createDomain(state), entries=new Map<string,HistoryEntry>();
  const add=(entry:HistoryEntry)=>{ if(!entries.has(entry.id))entries.set(entry.id,entry); };
  const areaFor=(ref: NonNullable<HistoryEntry['ref']>, date: string, group='')=>domain.eventArea({date,tipo:'protocolo',prioridad:2,label:'',doneKey:'',ref,protocolGroup:group});
  for(const birth of state.partos) add({id:`parto:${birth.id}`,date:birth.fecha,type:'parto',title:'Parto registrado',
    detail:[`${birth.nacidosVivos} vivos · ${birth.nacidosMuertos} muertos · ${birth.momias} momias`,birth.intervencionManual?'Intervención manual':null,birth.observaciones].filter(Boolean).join(' · '),
    subject:`Cerda ${birth.arete}`,ref:{type:'cerda',id:birth.arete},area:'maternidad',source:'Registro de parto'});
  for(const montage of state.montas) add({id:`monta:${montage.id}`,date:montage.fecha,type:'monta',title:montage.tipoServicio==='ia'?'Inseminación registrada':'Monta registrada',
    detail:domain.nombresVerracosDeMonta(montage),subject:`Cerda ${montage.arete}`,ref:{type:'cerda',id:montage.arete},area:'gestacion',source:'Registro reproductivo'});
  for(const repeat of state.repeticiones) add({id:`repeticion:${repeat.id}`,date:repeat.fecha,type:'repeticion',title:'Repetición registrada',
    detail:repeat.infeccion?'Con infección':'Sin infección registrada',subject:`Cerda ${repeat.arete}`,ref:{type:'cerda',id:repeat.arete},area:'gestacion',source:'Registro reproductivo'});
  for(const heat of state.celos) add({id:`celo:${heat.id}`,date:heat.fecha,type:'celo',title:'Celo registrado',detail:heat.observaciones,
    subject:`Cerda ${heat.arete}`,ref:{type:'cerda',id:heat.arete},area:'gestacion',source:'Registro reproductivo'});
  for(const feed of state.alimentacion) {
    const ref={type:'lote',id:feed.loteId};
    add({id:`alimentacion:${feed.id}`,date:feed.desde,type:'alimentacion',title:'Alimentación registrada',
      detail:[state.fasesAlimento.find(item=>item.id===feed.faseId)?.nombre || 'Fase no conservada',`${feed.libras} lb`,feed.hasta?`Hasta ${fmtDate(feed.hasta)}`:''].filter(Boolean).join(' · '),
      subject:`Lote ${feed.loteId}`,ref,area:areaFor(ref,feed.desde),source:'Registro de alimentación'});
  }
  for(const ration of state.racionesLactancia) add({id:`racion:${ration.id}`,date:ration.fechaDesde,type:'alimentacion',title:'Ración diaria de lactancia registrada',
    detail:`Cerda: ${ration.librasCerda} lb/día · Lechones: ${ration.librasLechones} lb/día`,subject:`Cerda ${ration.arete}`,ref:{type:'cerda',id:ration.arete},area:'maternidad',source:'Cambio de ración diaria'});
  for(const application of state.aplicacionesProcedimientos || []) add({id:`procedimiento:${application.id}`,date:application.fecha,type:'procedimiento',
    title:application.titulo,detail:[application.acciones,application.detalle].filter(Boolean).join(' · '),subject:application.subject,
    ref:{...application.ref},area:application.area,source:'Procedimiento aplicado'});
  const completedKeys=new Set<string>();
  const legacySnapshots=new Set<string>();
  const usedTaskIds=new Set<string>();
  const addTask=(event: FarmEvent, area: string, snapshot?: TareaRealizada)=>{
    if(!snapshot && completedKeys.has(event.doneKey))return;
    if(snapshot && !snapshot.id) {
      const identity=JSON.stringify({key:event.doneKey,event,area,completedAt:snapshot.completedAt,fechaRealizacion:snapshot.fechaRealizacion,legacy:snapshot.legacy});
      if(legacySnapshots.has(identity))return;legacySnapshots.add(identity);
    }
    // Even archived snapshots already account for this legacy key. A dangling old key is not
    // evidence of another execution; actual new completions always capture a new snapshot.
    completedKeys.add(event.doneKey);
    const baseId=`tarea:${snapshot?.id || event.doneKey}`;
    let id=baseId,suffix=1;
    while(usedTaskIds.has(id))id=`${baseId}:${++suffix}`;
    usedTaskIds.add(id);
    const legacy=!snapshot || snapshot.legacy===true;
    const warnings=legacy?'Fecha de realización no registrada; la fecha mostrada es la programada, si se conserva.':'';
    const isForecast=['parto','repeticion','celo'].includes(event.tipo);
    add({id,date:legacy?event.date:(snapshot?.fechaRealizacion || (snapshot?.completedAt?snapshot.completedAt.slice(0,10):event.date)),
      type:taskType(event),title:taskTitle(event),detail:[event.detail,warnings,isForecast?'Actividad de agenda terminada; no sustituye el registro reproductivo.':''].filter(Boolean).join(' · '),
      subject:domain.eventSubject(event),ref:taskRef(state,event),area,scheduledDate:event.date,completedAt:snapshot?.completedAt,legacy,
      ...(isFeedChangeEvent(event)?{category:FEED_CHANGE_CATEGORY}:{}),
      source:legacy?'Tarea terminada anterior (sin fecha real conservada)':'Tarea marcada como realizada'});
  };
  // Snapshots survive deleting a template or closing its reproductive cycle.
  for(const snapshot of [...(state.tareasRealizadas || [])].sort((a,b)=>(b.completedAt || '').localeCompare(a.completedAt || '')))addTask(snapshot.event,snapshot.area,snapshot);
  const activeEvents=new Map(domain.generateAllEvents().map(event=>[event.doneKey,event]));
  for(const key of new Set(state.tareasHechas)) {
    if(completedKeys.has(key))continue;
    const event=activeEvents.get(key) || recoverCompletedEvent(state,key);
    addTask(event,domain.eventArea(event));
  }
  return [...entries.values()].sort((a,b)=>b.date.localeCompare(a.date) || (b.completedAt || '').localeCompare(a.completedAt || '') || a.id.localeCompare(b.id));
}

export function filterHistory(state: FarmState, filter: HistoryFilter): HistoryEntry[] {
  const search=fold(filter.search?.trim() || '');
  return getHistory(state).filter(entry=>
    (!filter.area || filter.area==='todas' || entry.area===filter.area) &&
    (filter.type===FEED_CHANGE_CATEGORY?entry.category===FEED_CHANGE_CATEGORY:!filter.type || filter.type==='todos' || entry.type===filter.type) &&
    (!filter.from || (!!entry.date && entry.date>=filter.from)) &&
    (!filter.to || (!!entry.date && entry.date<=filter.to)) &&
    (!search || fold([entry.title,entry.detail,entry.subject,entry.source || ''].join(' ')).includes(search))
  );
}
